/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// Lib dependencies
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListX } from 'lucide-react';
import { DateRange } from 'react-day-picker';

// Hooks
import { useMeetings } from '../../hooks/meetings.hook';

// Components
import { MeetingCard } from '../MeetingCard';
import { DatePickerWithRange } from '@/components/Form/RangePickerWithRange';
import { Button } from '@/components/UI/Button';
import SelectField from '@/components/Form/SelectField';

// Types
import { GroupedMeetings, Meeting, MeetingStatus } from '@/types/meeting';

// Helpers
import { groupMeetingsByDate } from '@/helpers/date.helper';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
function CompletedMeetings() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [reportFilter, setReportFilter] = useState<
    'all' | 'completed' | 'todo'
  >('all');

  const { t } = useTranslation();

  const formatDate = (date?: Date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const { data, hasNextPage, isFetching, fetchNextPage, isPending } =
    useMeetings({
      params: {
        status: MeetingStatus.DONE,
        startDate: formatDate(dateRange?.from),
        endDate: formatDate(dateRange?.to),
        reportDone:
          reportFilter === 'completed'
            ? true
            : reportFilter === 'todo'
              ? false
              : undefined,
      },
    });

  const meetings = useMemo<Meeting[]>(
    () => data?.pages.flatMap((item) => item.results) || [],
    [data]
  );

  const groupedMeetings = useMemo<GroupedMeetings>(
    () => groupMeetingsByDate(meetings),
    [meetings]
  );

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetching, fetchNextPage]);

  const observerRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (!node || !hasNextPage) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            handleLoadMore();
          }
        },
        { threshold: 1.0 }
      );

      observer.observe(node);
      return () => observer.disconnect();
    },
    [hasNextPage, handleLoadMore]
  );

  const isMeetingsEmpty = Object.keys(groupedMeetings).length === 0;

  return (
    <div>
      <div className="flex flex-col">
        <div className="text-background-1">{t('global.filter')}</div>
        <div className="flex items-center w-full pb-4 gap-2">
          <SelectField
            bordered={true}
            defaultValue={reportFilter}
            options={[
              {
                label: 'Tous',
                value: 'all',
              },
              {
                label: 'Compte rendu à faire',
                value: 'todo',
              },
              {
                label: 'Compte rendu fait',
                value: 'completed',
              },
            ]}
            onChange={(e) => setReportFilter(e as 'all' | 'completed' | 'todo')}
            className="text-black"
            label={''}
            placeholder={''}
          />
          <DatePickerWithRange
            onDateChange={setDateRange}
            date={dateRange}
            className="text-black"
          />
          {dateRange && (
            <Button
              onClick={() => setDateRange(undefined)}
              variant="outlineDestructive"
              size="icon"
            >
              <ListX />
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-8">
        {isMeetingsEmpty && !isPending ? (
          <div className="text-center text-background-1">
            {t('pages.meetings.no_completed_meetings')}
          </div>
        ) : (
          Object.entries(groupedMeetings).map(([date, meetings]) => (
            <div key={date}>
              <h2 className="text-lg font-semibold text-background-1 mb-4">
                {t(date)}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {meetings.map((meeting) => (
                  <MeetingCard key={meeting.id} meeting={meeting} />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
      <div ref={observerRef} className="text-center py-4"></div>
    </div>
  );
}

export default CompletedMeetings;
